import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import {
  LoginNumber,
  LoginCode,
  LoginPassword,
  SetPassword,
  CustomerDashbord,
  ServiceDashbord,
  Customers,
  ServiceOne,
  PlateCapture,
  ServiceTwo,
  ServiceThree,
  UserPanel,
  ServiceWorkerRegister,
  SelectService,
  Cars,
  AddCars,
  Periodic,
  ServiceReview,
  LatestServices,
  ServiceRecent,
  Notifications,
} from "../Page/Index";

import { Display, BottomNavigation, ServiceWorkerBottomNavigation } from "../Components/Index";
import {
  isServiceWorkerSection,
  SERVICE_WORKER_SHARED_ROUTES,
} from "../Components/BottomNavigation/ServiceWorkerBottomNavigation";
import { useAuth } from "../Context/AuthContext";
import { getMyMechanicProfile } from "../services/api/mechanicApi";
import { useEffect, useState } from "react";
import ProtectedRoute from "./ProtectedRoute";

// Bottom bar stays hidden on public (auth) flows so login screens keep
// their original full-height layout. Every protected route keeps the bar,
// with the active tab derived from the current URL (refresh + browser
// back/forward work with no extra state).
const HIDDEN_BOTTOM_NAV_PREFIXES = ["/LoginNumber", "/LoginCode", "/LoginPassword", "/SetPassword", "/ServiceWorkerRegister"];

const CUSTOMER_NAV_PREFIXES = [
  "/notifications",
  "/Cars",
  "/AddCars",
  "/SelectService",
];

function isCustomerSection(pathname) {
  if (pathname === "/") return true;
  return CUSTOMER_NAV_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + "/"));
}

function isSharedServiceWorkerRoute(pathname) {
  return SERVICE_WORKER_SHARED_ROUTES.some((prefix) => pathname.startsWith(prefix));
}

function getSessionNavSection() {
  try {
    return sessionStorage.getItem("tasmeh:lastSection");
  } catch {
    return null;
  }
}

function getStoredNavSection() {
  try {
    return sessionStorage.getItem("tasmeh:lastSection") || localStorage.getItem("tasmeh:lastSection");
  } catch {
    return null;
  }
}

// Notifies AppLayout that section/mechanic hints in storage changed without
// a route change (e.g. UserPanel delete/register, ServiceWorkerRegister
// success). Pages call this right after writing tasmeh:* keys so the Bottom
// Navigation updates instantly on the same route.
export const NAV_CHANGED_EVENT = "tasmeh:nav-changed";

export function notifyNavChanged() {
  try {
    window.dispatchEvent(new Event(NAV_CHANGED_EVENT));
  } catch {}
}

// Shared pages follow the section the user is currently using:
// 1) in-session section hint wins (where the user came from — switching
//    between customer/worker sections updates the bar correctly even for
//    registered workers browsing customer pages and vice versa);
// 2) otherwise the mechanic profile decides (direct navigation / refresh
//    with no in-session history);
// 3) otherwise a stale cross-session hint is the last resort.
// Pure helper (exported for tests): worker-only routes always use the
// worker bar, non-shared routes always use the customer bar, shared routes
// (/UserPanel, /LatestServices) follow the section hints above.
export function resolveServiceWorkerNav({ pathname, sessionSection, storedIsServiceWorker, effectiveIsMechanic }) {
  // Periodic is shared by both flows: customer registration keeps the
  // customer navigation, while the worker flow keeps the worker navigation.
  if (pathname.startsWith("/Periodic")) {
    if (sessionSection === "serviceWorker") return true;
    if (sessionSection === "customer") return false;
    if (effectiveIsMechanic === true) return true;
    if (effectiveIsMechanic === false) return false;
    return storedIsServiceWorker;
  }
  if (isServiceWorkerSection(pathname)) return true;
  if (!isSharedServiceWorkerRoute(pathname)) return false;
  if (sessionSection === "serviceWorker") return true;
  if (sessionSection === "customer") return false;
  if (effectiveIsMechanic === true) return true;
  if (effectiveIsMechanic === false) return false;
  return storedIsServiceWorker;
}

function getCachedIsMechanic() {
  try {
    const v = sessionStorage.getItem("tasmeh:isMechanic") || localStorage.getItem("tasmeh:isMechanic");
    if (v === "true") return true;
    if (v === "false") return false;
  } catch {}
  return null;
}

function AppLayout() {
  const { pathname } = useLocation();
  const { token, loading: authLoading } = useAuth();
  const showBottomNav = !HIDDEN_BOTTOM_NAV_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix)
  );

  const [isMechanic, setIsMechanic] = useState(() => getCachedIsMechanic());
  const [checkingMechanic, setCheckingMechanic] = useState(false);
  // Bumped whenever storage hints change without a route change (custom
  // NAV_CHANGED_EVENT / cross-tab storage event) so shared routes re-read
  // the fresh session/lastSection + isMechanic synchronously on the same
  // route (e.g. UserPanel delete/register flips the bar instantly).
  const [navTick, setNavTick] = useState(0);
  // Read on every render: bumping navTick re-renders so the live storage
  // reads below pick up same-route updates.
  void navTick;

  // Re-sync on section/mechanic hint changes that don't navigate.
  useEffect(() => {
    const resync = () => {
      setIsMechanic(getCachedIsMechanic());
      setNavTick((t) => t + 1);
    };
    let onStorage = null;
    try {
      onStorage = (e) => {
        if (!e.key || e.key.startsWith("tasmeh:")) resync();
      };
      window.addEventListener("storage", onStorage);
      window.addEventListener(NAV_CHANGED_EVENT, resync);
    } catch {}
    return () => {
      try {
        if (onStorage) window.removeEventListener("storage", onStorage);
        window.removeEventListener(NAV_CHANGED_EVENT, resync);
      } catch {}
    };
  }, []);

  // Reliable service-worker detection for direct navigation: if the user
  // has an active mechanic profile (GET /mechanics/me succeeds), they are
  // a service worker. Cached synchronously for immediate render, refreshed
  // async when authenticated so a cold direct navigation to /UserPanel
  // still resolves to the worker bar without needing prior history.
  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      setIsMechanic(false);
      setCheckingMechanic(false);
      try {
        localStorage.removeItem("tasmeh:isMechanic");
        sessionStorage.removeItem("tasmeh:isMechanic");
      } catch {}
      return;
    }
    let cancelled = false;
    const cached = getCachedIsMechanic();
    if (cached === null) setCheckingMechanic(true);
    // Optimistically keep cached value until fetch confirms; no loading flash
    // for returning workers. For unknown users the fetch determines the bar.
    getMyMechanicProfile()
      .then((res) => {
        if (cancelled) return;
        const ok = !!(res?.success && res?.data);
        setIsMechanic(ok);
        setCheckingMechanic(false);
        try {
          localStorage.setItem("tasmeh:isMechanic", ok ? "true" : "false");
          sessionStorage.setItem("tasmeh:isMechanic", ok ? "true" : "false");
          // Warm the section hint for direct navigation ONLY when there is
          // no in-session hint yet. Never overwrite an in-session customer
          // section: a registered worker browsing customer pages must keep
          // the customer bar on /UserPanel.
          if (ok && !getSessionNavSection()) {
            localStorage.setItem("tasmeh:lastSection", "serviceWorker");
            sessionStorage.setItem("tasmeh:lastSection", "serviceWorker");
          }
        } catch {}
      })
      .catch(() => {
        if (cancelled) return;
        setIsMechanic(false);
        setCheckingMechanic(false);
        try {
          localStorage.setItem("tasmeh:isMechanic", "false");
          sessionStorage.setItem("tasmeh:isMechanic", "false");
        } catch {}
      });
    return () => {
      cancelled = true;
    };
  }, [token, authLoading]);

  // Persist last visited section so shared pages (/UserPanel, /LatestServices)
  // keep the correct bar across route changes and refresh. Strict worker
  // prefixes set serviceWorker, strict customer prefixes set customer;
  // shared routes never overwrite so back/forward stays consistent.
  // (navTick forces a re-render on same-route storage updates; the fresh
  // section is re-read in the render body below, no effect re-run needed.)
  useEffect(() => {
    try {
      if (isServiceWorkerSection(pathname) && !pathname.startsWith("/Periodic")) {
        localStorage.setItem("tasmeh:lastSection", "serviceWorker");
        sessionStorage.setItem("tasmeh:lastSection", "serviceWorker");
      } else if (isCustomerSection(pathname)) {
        localStorage.setItem("tasmeh:lastSection", "customer");
        sessionStorage.setItem("tasmeh:lastSection", "customer");
      }
    } catch {}
  }, [pathname]);

  // Live reads so same-route storage updates (delete/register) reflect
  // immediately once the resync above re-renders.
  const sessionSection = getSessionNavSection();
  const storedSection = getStoredNavSection();
  const storedIsServiceWorker = storedSection === "serviceWorker";
  const isSharedRoute = isSharedServiceWorkerRoute(pathname);
  const cachedIsMechanic = getCachedIsMechanic();
  const effectiveIsMechanic = cachedIsMechanic !== null ? cachedIsMechanic : isMechanic;

  // Shared pages follow the section the user is currently using (see
  // resolveServiceWorkerNav above for the priority order).
  const showServiceWorkerNav = resolveServiceWorkerNav({
    pathname,
    sessionSection,
    storedIsServiceWorker,
    effectiveIsMechanic,
  });

  // On shared routes with unknown mechanic status and no stored hint,
  // hide the bar until the mechanic check resolves — prevents the customer
  // bar from flashing on a worker's direct navigation to /UserPanel
  // (requirement: customer bar must never appear on worker pages).
  const isUnknownShared = isSharedRoute && effectiveIsMechanic === null && !storedIsServiceWorker;
  const shouldHideNavDuringCheck =
    isUnknownShared && (checkingMechanic || authLoading || (!!token && cachedIsMechanic === null));

  return (
    <Display>
      <Routes>
        <Route path="/LoginNumber" element={<LoginNumber />} />

        <Route path="/LoginPassword" element={<LoginPassword />} />

        <Route path="/LoginCode" element={<LoginCode />} />

        <Route path="/SetPassword" element={<SetPassword />} />

        <Route
          path="/SelectService"
          element={
            <ProtectedRoute>
              <SelectService />
            </ProtectedRoute>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <CustomerDashbord />
            </ProtectedRoute>
          }
        />

        <Route path="/Customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />

        <Route
          path="/ServiceDashbord"
          element={
            <ProtectedRoute>
              <ServiceDashbord />
            </ProtectedRoute>
          }
        />

        <Route
          path="/UserPanel"
          element={
            <ProtectedRoute>
              <UserPanel />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ServiceWorkerRegister"
          element={
            <ProtectedRoute>
              <ServiceWorkerRegister />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Cars"
          element={
            <ProtectedRoute>
              <Cars />
            </ProtectedRoute>
          }
        />

        {/* =========================
            اضافه کردن ماشین
        ========================= */}

        <Route
          path="/AddCars"
          element={
            <ProtectedRoute>
              <AddCars />
            </ProtectedRoute>
          }
        />

        {/* =========================
            ویرایش ماشین
            مثال:
            /AddCars/12
        ========================= */}

        <Route
          path="/AddCars/:carId"
          element={
            <ProtectedRoute>
              <AddCars />
            </ProtectedRoute>
          }
        />

        <Route
          path="/PlateCapture"
          element={
            <ProtectedRoute>
              <PlateCapture />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ServiceOne"
          element={
            <ProtectedRoute>
              <ServiceOne />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ServiceTwo"
          element={
            <ProtectedRoute>
              <ServiceTwo />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ServiceThree"
          element={
            <ProtectedRoute>
              <ServiceThree />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Periodic/:categoryId"
          element={
            <ProtectedRoute>
              <Periodic />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ServiceReview/:categoryId"
          element={<ProtectedRoute><ServiceReview /></ProtectedRoute>}
        />
        <Route
          path="/LatestServices"
          element={
            <ProtectedRoute>
              <LatestServices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ServiceRecent"
          element={
            <ProtectedRoute>
              <ServiceRecent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showBottomNav && !shouldHideNavDuringCheck && (showServiceWorkerNav ? <ServiceWorkerBottomNavigation /> : <BottomNavigation />)}
    </Display>
  );
}

function AppRoute() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}

export default AppRoute;
